import React, { useState } from 'react';
import SupplementDetailsModal from './SupplementDetailsModal';
import ConfirmationModal from '@/components/ConfirmationModal';

interface Prompt {
  id: number;
  prompt_text: string;
  word_count: number;
  primary_category_id: number;
  secondary_category_id: number;
  ai_prompt_specific_advice: string;
}

type SupplementStatus = 'OPTIONAL' | 'REQUIRED' | 'HIGHLY_RECOMMENDED';

interface Supplement {
  id: number;
  name: string;
  description?: string;
  min_number_required: number;
  max_number_required: number;
  status: SupplementStatus;
  ai_specific_advice?: string;
  prompts?: Prompt[];
}

interface PromptCategory {
  id: number;
  name: string;
  overview?: string;
}

interface UniversitySupplementsProps {
  supplements: Supplement[];
  onSupplementUpdate?: (supplement: Supplement) => Promise<void>;
  onSupplementCreate?: (supplementData: Partial<Supplement>) => Promise<void>;
  onSupplementDelete?: (supplementId: number) => Promise<void>;
  promptCategories: PromptCategory[];
  universityId?: number;
}

const UniversitySupplements: React.FC<UniversitySupplementsProps> = ({ 
  supplements, 
  onSupplementUpdate,
  onSupplementCreate,
  onSupplementDelete,
  promptCategories,
  universityId
}) => {
  const [selectedSupplement, setSelectedSupplement] = useState<Supplement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [supplementToDelete, setSupplementToDelete] = useState<number | null>(null);

  const handleSupplementClick = (supplement: Supplement) => {
    setSelectedSupplement(supplement);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedSupplement(null);
    setIsCreating(false);
  };

  const handleSupplementSave = async (updatedSupplement: Supplement) => {
    if (isCreating && onSupplementCreate) {
      await onSupplementCreate(updatedSupplement);
    } else if (onSupplementUpdate) {
      await onSupplementUpdate(updatedSupplement);
    }
    setIsCreating(false);
  };

  const handleAddNewSupplement = () => {
    if (!universityId) return;
    
    const newSupplement: Supplement = {
      id: 0,
      name: '',
      description: '',
      min_number_required: 1,
      max_number_required: 1,
      status: 'OPTIONAL',
      ai_specific_advice: '',
      prompts: []
    };
    
    setSelectedSupplement(newSupplement);
    setIsCreating(true);
    setIsModalOpen(true);
  };

  const handleDeleteSupplement = (supplementId: number, e: React.MouseEvent) => {
    e.stopPropagation();    
    setSupplementToDelete(supplementId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);
    if (supplementToDelete && onSupplementDelete) {
      await onSupplementDelete(supplementToDelete);
    }
    setSupplementToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSupplementToDelete(null);
  };

  return (
    <div className="mt-8 bg-white rounded-lg shadow-lg">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">University Supplements</h3>
          {universityId && (
            <button
              onClick={handleAddNewSupplement}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Add New Supplement</span>
            </button>
          )}
        </div>
        
        {supplements.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {supplements.map((supplement) => (
              <div 
                key={supplement.id} 
                onClick={() => handleSupplementClick(supplement)}
                className="bg-gray-50 border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-100 transition-colors relative"
              >
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-semibold text-gray-900">
                    {supplement.name}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      supplement.status === 'REQUIRED' 
                        ? 'bg-blue-100 text-blue-800' 
                        : supplement.status === 'HIGHLY_RECOMMENDED'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {supplement.status === 'REQUIRED' ? 'Required' : 
                       supplement.status === 'HIGHLY_RECOMMENDED' ? 'Highly Recommended' : 'Optional'}
                    </span>
                    <button
                      onClick={(e) => handleDeleteSupplement(supplement.id, e)}
                      className="text-red-500 hover:text-red-700 p-1 rounded transition-colors"
                      title="Delete supplement"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {supplement.description && (
                  <p className="text-sm text-gray-600 mb-3">
                    {supplement.description}
                  </p>
                )}
                
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <span className="font-medium">
                    {supplement.prompts?.length || 0} prompts
                  </span>
                </div>
                
                {supplement.ai_specific_advice && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-xs text-blue-700">
                      <strong>AI Advice:</strong> {supplement.ai_specific_advice}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No supplements found for this university.</p>
            {universityId && (
              <p className="text-sm mt-2">Click "Add New Supplement" to create the first supplement.</p>
            )}
          </div>
        )}
      </div>

      <SupplementDetailsModal
        supplement={selectedSupplement}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSupplementSave}
        promptCategories={promptCategories}
        supplements={supplements}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Supplement"
        message="Are you sure you want to delete this supplement? This will also delete all associated prompts."
        confirmText="OK"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmButtonColor="red"
      />
    </div>
  );
};

export default UniversitySupplements;